import { useMedplum } from '@medplum/react';
import { Basic, Schedule } from '@medplum/fhirtypes';
import { IVR_BASIC_SYSTEM, IVR_HOURS_SET_DEFAULT_NAMES, IvrHoursSetId, IvrTemplateId } from '@bariendo/lib/constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IvrTemplate,
  IvrHoursSet,
  IvrMenuSettings,
  DEFAULT_IVR_MENU_SETTINGS,
  parseIvrTemplateBasic,
  buildIvrTemplateBasic,
  buildIvrMenuSettingsBasic,
  parseIvrMenuSettingsBasic,
  applyHoursSetToSchedule,
  parseHoursSetFromSchedule,
  getIvrHoursSetId,
  fetchIvrConfigResources,
  fetchIvrHoursSetSchedules,
  fetchHeadquartersLocation,
} from '@bariendo/lib/ivr';
import { IVR_SYSTEM_CONFIG_BASIC_CODING } from '@bariendo/lib/codings';
import { getOperationOutcomeMessage, isForbiddenError } from '@bariendo/lib/error';

interface TemplateEntry {
  parsed: IvrTemplate;
  basic: Basic;
}

interface HoursSetEntry {
  parsed: IvrHoursSet;
  schedule: Schedule;
}

export interface IvrConfigLoadError {
  /** "permission" means the caller's AccessPolicy denied a resource, not that data is missing. */
  kind: 'permission' | 'unknown';
  message: string;
}

export interface UseIVRConfigReturn {
  loading: boolean;
  hoursSets: Map<IvrHoursSetId, IvrHoursSet>;
  /** Which sets exist as saved Schedules, as opposed to placeholders offered for creation. */
  configuredHoursSetIds: Set<IvrHoursSetId>;
  menuSettings: IvrMenuSettings;
  languageSwitchKey: number;
  templateEntries: Map<IvrTemplateId, TemplateEntry>;
  loadError: IvrConfigLoadError | undefined;
  saveHoursSet: (hoursSet: IvrHoursSet) => Promise<void>;
  saveMenuSettings: (settings: IvrMenuSettings) => Promise<void>;
  saveTemplate: (templateId: IvrTemplateId, template: IvrTemplate) => Promise<void>;
}

const PERMISSION_ERROR_MESSAGE =
  'You do not have permission to view or edit the IVR configuration. This requires an administrator account.';

function toLoadError(errors: unknown[]): IvrConfigLoadError | undefined {
  const failures = errors.filter((error) => error !== undefined);
  if (failures.length === 0) {
    return undefined;
  }

  if (failures.some(isForbiddenError)) {
    return { kind: 'permission', message: PERMISSION_ERROR_MESSAGE };
  }

  return { kind: 'unknown', message: getOperationOutcomeMessage(failures[0]) };
}

export function useIVRConfig(): UseIVRConfigReturn {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [hoursSetEntries, setHoursSetEntries] = useState<Map<IvrHoursSetId, HoursSetEntry>>(new Map());
  const [menuSettings, setMenuSettings] = useState<IvrMenuSettings>(DEFAULT_IVR_MENU_SETTINGS);
  const [settingsBasic, setSettingsBasic] = useState<Basic | undefined>();
  const [templateEntries, setTemplateEntries] = useState<Map<IvrTemplateId, TemplateEntry>>(new Map());
  const [loadError, setLoadError] = useState<IvrConfigLoadError | undefined>();
  const [scheduleDenied, setScheduleDenied] = useState(false);
  const templateEntriesRef = useRef(templateEntries);
  const hoursSetEntriesRef = useRef(hoursSetEntries);

  templateEntriesRef.current = templateEntries;
  hoursSetEntriesRef.current = hoursSetEntries;

  useEffect(() => {
    async function load() {
      setLoading(true);

      try {
        const [schedulesResult, templatesResult, settingsResult] = await Promise.allSettled([
          fetchIvrHoursSetSchedules(medplum),
          fetchIvrConfigResources(medplum),
          medplum.searchOne('Basic', {
            code: `${IVR_BASIC_SYSTEM}|${IVR_SYSTEM_CONFIG_BASIC_CODING.code}`,
          }),
        ]);

        const schedules = schedulesResult.status === 'fulfilled' ? schedulesResult.value : [];
        const entries = new Map<IvrHoursSetId, HoursSetEntry>();

        for (const schedule of schedules) {
          const parsed = parseHoursSetFromSchedule(schedule);

          if (!parsed) {
            continue;
          }

          // Same precedence as fetchIvrHoursSets: an explicitly identified Schedule beats
          // the unidentified legacy one that only defaulted into this slot.
          const isExplicit = !!getIvrHoursSetId(schedule);
          if (entries.has(parsed.hoursSetId) && !isExplicit) {
            continue;
          }

          entries.set(parsed.hoursSetId, { parsed, schedule });
        }

        setHoursSetEntries(entries);

        const templateBasics = templatesResult.status === 'fulfilled' ? templatesResult.value.templateBasics : [];
        const templates = new Map<IvrTemplateId, TemplateEntry>();
        for (const basic of templateBasics) {
          try {
            const parsed = parseIvrTemplateBasic(basic);
            templates.set(parsed.templateId, { parsed, basic });
          } catch {
            // ignore malformed template resources
          }
        }
        setTemplateEntries(templates);

        const settings = settingsResult.status === 'fulfilled' ? settingsResult.value : undefined;
        if (settings) {
          setSettingsBasic(settings);
          setMenuSettings(parseIvrMenuSettingsBasic(settings));
        } else if (schedules[0]) {
          // parseIvrMenuSettingsBasic only reads the two key extensions, which the legacy
          // Schedule still carries. This keeps the configured keys visible until the first
          // save moves them onto the config Basic.
          setMenuSettings(parseIvrMenuSettingsBasic(schedules[0] as unknown as Basic));
        }

        const scheduleError = schedulesResult.status === 'rejected' ? schedulesResult.reason : undefined;
        const templatesError =
          templatesResult.status === 'rejected' ? templatesResult.reason : templatesResult.value.templatesError;

        setScheduleDenied(isForbiddenError(scheduleError));
        setLoadError(toLoadError([scheduleError, templatesError]));
      } catch (error) {
        setLoadError(toLoadError([error]));
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [medplum]);

  const saveHoursSet = useCallback(
    async (hoursSet: IvrHoursSet) => {
      const existing = hoursSetEntriesRef.current.get(hoursSet.hoursSetId);

      let saved: Schedule;

      if (existing) {
        saved = await medplum.updateResource(applyHoursSetToSchedule(existing.schedule, hoursSet));
      } else {
        if (scheduleDenied) {
          throw new Error(PERMISSION_ERROR_MESSAGE);
        }

        // A new set needs the headquarters actor, or the searches that read hours back
        // will not match it and the set will look like it silently failed to save.
        const headquarters = await fetchHeadquartersLocation(medplum);

        if (!headquarters) {
          throw new Error('Headquarters location not found. An IVR hours set must be attached to it.');
        }

        saved = await medplum.createResource(
          applyHoursSetToSchedule(
            {
              resourceType: 'Schedule',
              active: true,
              actor: [{ reference: `Location/${headquarters.id}` }],
            },
            hoursSet
          )
        );
      }

      const parsed = parseHoursSetFromSchedule(saved) ?? hoursSet;

      setHoursSetEntries((prev) => {
        const next = new Map(prev);
        next.set(hoursSet.hoursSetId, { parsed, schedule: saved });
        return next;
      });
    },
    [medplum, scheduleDenied]
  );

  const saveMenuSettings = useCallback(
    async (settings: IvrMenuSettings) => {
      const built = buildIvrMenuSettingsBasic(settings);
      const saved = settingsBasic?.id
        ? await medplum.updateResource({ ...built, id: settingsBasic.id })
        : await medplum.createResource(built);

      setSettingsBasic(saved);
      setMenuSettings(parseIvrMenuSettingsBasic(saved));
    },
    [medplum, settingsBasic]
  );

  const saveTemplate = useCallback(
    async (templateId: IvrTemplateId, template: IvrTemplate) => {
      const built = buildIvrTemplateBasic(template);
      const existing = templateEntriesRef.current.get(templateId);
      const updated = existing?.basic.id
        ? await medplum.updateResource({ ...built, id: existing.basic.id })
        : await medplum.createResource(built);

      setTemplateEntries((prev) => {
        const next = new Map(prev);
        next.set(templateId, { parsed: template, basic: updated });
        return next;
      });
    },
    [medplum]
  );

  const hoursSets = useMemo(() => {
    const result = new Map<IvrHoursSetId, IvrHoursSet>();

    for (const [id, entry] of hoursSetEntries) {
      result.set(id, entry.parsed);
    }

    // Every known set is surfaced, configured or not, so an unconfigured one can still be
    // filled in from the drawer instead of needing a Schedule seeded by hand first.
    for (const id of Object.values(IvrHoursSetId)) {
      if (!result.has(id)) {
        result.set(id, { hoursSetId: id, name: IVR_HOURS_SET_DEFAULT_NAMES[id] });
      }
    }

    return result;
  }, [hoursSetEntries]);

  const configuredHoursSetIds = useMemo(() => new Set(hoursSetEntries.keys()), [hoursSetEntries]);

  return {
    loading,
    hoursSets,
    configuredHoursSetIds,
    menuSettings,
    languageSwitchKey: menuSettings.languageSwitchKey,
    templateEntries,
    loadError,
    saveHoursSet,
    saveMenuSettings,
    saveTemplate,
  };
}
